import 'react-datepicker/dist/react-datepicker.css';

import {
	addMonths,
	addWeeks,
	format,
	isMonday,
	nextSunday,
	previousMonday,
	subDays,
} from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import { forwardRef, HTMLProps, useCallback,useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useSearchParams } from 'react-router-dom';

import Arrow2 from '@/components/ui/icons/Arrow2';
import Calendar2 from '@/components/ui/icons/Calendar2';
import { createParams } from '@/helpers/createParams';
import { splitDate } from '@/helpers/dates';

import classes from './CalendarDatePicker.module.scss';

registerLocale('ru', ru);

const ExampleCustomInput = forwardRef<
	HTMLButtonElement,
	HTMLProps<HTMLButtonElement> & {
		isActive: boolean;
		buttonClass?: string;
		selectedOption: string;
		startDate: Date;
		endDate: Date | null;
	}
>(({ onClick, isActive, buttonClass, selectedOption, startDate, endDate }, ref) => {
	const toUpperFirstLetter = useCallback(
		(str: string) => str.replace(str[0], str[0].toUpperCase()),
		[]
	);

	const getDateDisplay = useCallback(() => {
		if (selectedOption === 'day') {
			return (
				startDate.toLocaleDateString() +
				(endDate ? ' - ' + endDate?.toLocaleDateString() : '')
			);
		}
		if (selectedOption === 'week') {
			return toUpperFirstLetter(format(startDate, 'LLLL', { locale: ru }));
		}
		if (selectedOption === 'month') {
			return format(startDate, 'yyyy');
		}
		return '';
	}, [selectedOption, startDate, endDate, toUpperFirstLetter]);

	return (
		<button
			className={`${buttonClass} ${isActive ? classes.activeInput : classes.input}`}
			ref={ref}
			onClick={onClick}
		>
			<div className={classes.date}>
				<Calendar2 clazz={classes.calendarIcon} />
				{getDateDisplay()}
			</div>
			<Arrow2 clazz={classes.arrowIcon} />
		</button>
	);
});

ExampleCustomInput.displayName = 'ExampleCustomInput';

const MONTH_CONTENT = (_month: number, _shortMonth: string, longMonth: string) => longMonth;

const RADIO_OPTIONS = [
	{ value: 'day', label: 'Неделя' },
	{ value: 'week', label: 'Месяц' },
	{ value: 'month', label: 'Год' },
] as const;

type Props = {
	buttonClass?: string;
};

const CalendarDatePicker = ({ buttonClass }: Props) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [isActive, setIsActive] = useState(false);
	const [selectedOption, setSelectedOption] = useState(
		() => searchParams.get('time_unit') || 'day'
	);
	const [startDate, setStartDate] = useState(() =>
		searchParams.get('date_start') ? new Date(searchParams.get('date_start')!) : new Date()
	);
	const [endDate, setEndDate] = useState<Date | null>(() =>
		searchParams.get('date_end') ? new Date(searchParams.get('date_end')!) : null
	);

	const handleChangeDate = useCallback(
		(date: Date | null) => {
			if (!date) return;

			let start = date;
			let end = date;

			const params: Record<string, string> = {};

			if (selectedOption === 'day') {
				start = isMonday(date) ? date : previousMonday(date);
				end = addWeeks(start, 1);
				end = subDays(end, 1);
				params.time_unit = 'day';
			}

			if (selectedOption === 'week') {
				start = isMonday(date) ? date : previousMonday(date);
				end = addMonths(start, 1);
				end = nextSunday(end);
				params.time_unit = 'week';
				start = date;
			}

			if (selectedOption === 'month') {
				end = addMonths(date, 12);
				end = subDays(end, 1);
				params.time_unit = 'month';
			}

			params.date_start = splitDate(start.toLocaleDateString());
			params.date_end = splitDate(end.toLocaleDateString());

			setStartDate(start);
			setEndDate(end);
			setSearchParams(createParams(searchParams, params));
		},
		[selectedOption, searchParams, setSearchParams]
	);

	const handleOptionChange = useCallback((value: string) => {
		setSelectedOption(value);
	}, []);

	const renderCustomHeader = useCallback(
		() => (
			<div className={classes.radioContainer}>
				{RADIO_OPTIONS.map(({ value, label }) => (
					<div
						key={value}
						className={classes.radio}
						onClick={() => handleOptionChange(value)}
					>
						<input
							type='radio'
							value={value}
							name={value}
							checked={selectedOption === value}
							readOnly
						/>
						<label className={classes.label} htmlFor={value}>
							{label}
						</label>
					</div>
				))}
			</div>
		),
		[selectedOption, handleOptionChange]
	);

	return (
		<div className={classes.calendar}>
			<DatePicker
				calendarClassName={classes.calendarContainer}
				selected={startDate}
				onChange={handleChangeDate}
				customInput={
					<ExampleCustomInput
						isActive={isActive}
						buttonClass={buttonClass}
						selectedOption={selectedOption}
						startDate={startDate}
						endDate={endDate}
					/>
				}
				onCalendarOpen={() => setIsActive(true)}
				onCalendarClose={() => setIsActive(false)}
				locale='ru'
				dateFormat='dd.MM.yyyy'
				renderMonthContent={MONTH_CONTENT}
				yearItemNumber={6}
				showWeekPicker={selectedOption === 'day'}
				showMonthYearPicker={selectedOption === 'week'}
				showYearPicker={selectedOption === 'month'}
				renderCustomHeader={renderCustomHeader}
			/>
		</div>
	);
};

export default CalendarDatePicker;
